import React, {createContext, useContext, useState} from 'react';
import {createPortal} from 'react-dom';
import {CheckCircle, ChevronDown, ChevronRight, Plus, Save, Search, Trash2} from 'lucide-react';
import {HeaderAction, ListEmpty, ListLoader, thClass, mobileRowClass, RowActions} from '../../stock/StockUI';
import Button from '../Common/Button';

// Building blocks of the settings sections. They reproduce the catalogue list
// (search toolbar edge to edge, square table card, "Actions ▾" menu, mobile cards)
// so every section of the settings reads like the rest of the backoffice.

export {HeaderAction, ListLoader};

// First row of a list section: a 64px search bar — the height of the section
// title bar and of the sidebar rows, so the borders line up across the page —
// with its icon under the section title icon (px-4 / sm:px-6). Extra filters go
// in `children`; the "Ajouter" action belongs in the title bar (AddAction).
export const SettingsToolbar = ({search, onSearch, placeholder, children}) => (
    <div className="flex flex-col sm:flex-row sm:h-16 border-b border-gray-200 dark:border-gray-700">
        {onSearch && (
            <div className="relative flex-1 h-14 sm:h-auto">
                <Search className="w-5 h-5 absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    className="w-full h-full pl-11 sm:pl-[3.25rem] pr-4 text-sm sm:text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 border-0 focus:ring-2 focus:ring-inset focus:ring-primary transition-all"
                />
            </div>
        )}
        {children && (
            <div className={`flex flex-wrap items-center gap-4 px-4 sm:px-6 py-2.5 sm:py-0 bg-white dark:bg-gray-800 ${onSearch ? 'border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700' : 'flex-1'}`}>
                {children}
            </div>
        )}
    </div>
);

// "Ajouter" in the section title bar — the same add button for every section
export const AddAction = ({onClick, label = 'Ajouter'}) => (
    <SectionActions>
        <Button variant="plain" size="sm" icon={Plus} onClick={onClick}>{label}</Button>
    </SectionActions>
);

// Teal bar shown while rows are selected
export const SelectionBar = ({count, singular, plural, onDelete, deleting}) => count === 0 ? null : (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20 border-b border-primary/20 selection-bar-appear">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-full shadow">
                <CheckCircle className="w-4 h-4 text-white"/>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {count} {count > 1 ? plural : singular}
            </span>
        </div>
        <Button variant="danger" icon={Trash2} loading={deleting} onClick={onDelete}>
            Supprimer la sélection
        </Button>
    </div>
);

export const StatusBadge = ({active, on = 'Actif', off = 'Inactif'}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        active
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }`}>
        {active ? on : off}
    </span>
);

const checkboxClass = 'w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary';

// Table on desktop, cards on mobile.
// columns: [{key, label, render(row), className?, mobile?: false}]; the first column is the card title.
// selection (optional): {selected: ids[], onToggle(id), allSelected, onToggleAll}
// groups (optional, instead of rows): [{key, title, count, rows}] — collapsible sections sharing one header
export const SettingsTable = ({columns, rows: flatRows, groups, rowKey = 'id', selection, rowActions, onRowClick, empty, children}) => {
    const rows = groups ? groups.flatMap(group => group.rows) : flatRows;
    const [collapsed, setCollapsed] = useState({});
    const toggleGroup = (key) => setCollapsed(prev => ({...prev, [key]: !prev[key]}));
    const groupButton = (group) => {
        const Chevron = collapsed[group.key] ? ChevronRight : ChevronDown;
        return (
            <button type="button" aria-expanded={!collapsed[group.key]} onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center gap-2 px-4 sm:px-6 py-2.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <Chevron className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                <span className="truncate">{group.title}</span>
                <span className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{group.count}</span>
            </button>
        );
    };
    const [titleCol, ...otherCols] = columns;
    const isSelected = (row) => selection?.selected.includes(row[rowKey]);
    const rowClass = (row) => `transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${
        isSelected(row) ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`;
    const stop = (e) => e.stopPropagation();

    const desktopRow = (row) => (
        <tr key={row[rowKey]} className={rowClass(row)} onClick={onRowClick ? () => onRowClick(row) : undefined}>
            {selection && (
                <td className="pl-6 pr-2 py-4" onClick={stop}>
                    <input type="checkbox" className={checkboxClass}
                           checked={isSelected(row)}
                           onChange={() => selection.onToggle(row[rowKey])}/>
                </td>
            )}
            {columns.map((col, i) => (
                <td key={col.key}
                    className={`px-6 py-4 text-sm ${i === 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} ${col.className || ''}`}>
                    {col.render(row)}
                </td>
            ))}
            {rowActions && (
                <td className="px-6 py-4 text-right" onClick={stop}>
                    <RowActions actions={rowActions(row)}/>
                </td>
            )}
        </tr>
    );

    const mobileRow = (row) => (
        <div key={row[rowKey]}
             className={`${mobileRowClass} ${onRowClick ? 'cursor-pointer' : ''} ${isSelected(row) ? '!bg-primary/5 dark:!bg-primary/10' : ''}`}
             onClick={onRowClick ? () => onRowClick(row) : undefined}>
            <div className="flex items-start gap-3">
                {selection && (
                    <input type="checkbox" className={`mt-1 ${checkboxClass}`}
                           checked={isSelected(row)}
                           onClick={stop}
                           onChange={() => selection.onToggle(row[rowKey])}/>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100 min-w-0">
                        {titleCol.render(row)}
                    </div>
                    <dl className="mt-1.5 space-y-1">
                        {otherCols.filter(col => col.mobile !== false).map(col => (
                            <div key={col.key} className="flex items-center gap-2 text-xs min-w-0">
                                <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{col.label} :</dt>
                                <dd className="text-gray-700 dark:text-gray-300 min-w-0 truncate">{col.render(row)}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
                {rowActions && (
                    <div onClick={stop}>
                        <RowActions actions={rowActions(row)}/>
                    </div>
                )}
            </div>
        </div>
    );

    const columnCount = columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0);

    // Not a ListCard: its overflow clipping would cut the "Actions" menu of short lists.
    // Edge to edge under the toolbar: only a bottom border, no frame or shadow.
    return (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            {rows.length === 0 ? (
                <ListEmpty {...empty}/>
            ) : (
                <>
                    <div className="hidden md:block">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                                {selection && (
                                    <th className="w-12 pl-6 pr-2 py-4 text-left">
                                        <input type="checkbox" className={checkboxClass} aria-label="Tout sélectionner"
                                               checked={selection.allSelected && rows.length > 0}
                                               onChange={selection.onToggleAll}/>
                                    </th>
                                )}
                                {columns.map(col => (
                                    <th key={col.key} className={`${thClass} ${col.className || ''}`}>{col.label}</th>
                                ))}
                                {rowActions && <th className={`${thClass} text-right`}>Actions</th>}
                            </tr>
                            </thead>
                            {groups ? groups.map(group => (
                                <tbody key={group.key} className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr>
                                    <td colSpan={columnCount} className="p-0">{groupButton(group)}</td>
                                </tr>
                                {!collapsed[group.key] && group.rows.map(desktopRow)}
                                </tbody>
                            )) : (
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {rows.map(desktopRow)}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                    <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                        {groups ? groups.map(group => (
                            <div key={group.key} className="divide-y divide-gray-200 dark:divide-gray-700">
                                {groupButton(group)}
                                {!collapsed[group.key] && group.rows.map(mobileRow)}
                            </div>
                        )) : rows.map(mobileRow)}
                    </div>
                </>
            )}
            {children}
        </div>
    );
};

// Plain titled block of a settings form (catalogue section separator)
export const FormSection = ({title, description, children}) => (
    <section className="pt-4 sm:pt-5 first:pt-0 border-t first:border-t-0 border-gray-200 dark:border-gray-700 space-y-4">
        {(title || description) && (
            <div>
                {title && <h3 className="text-base font-semibold text-gray-800 dark:text-white">{title}</h3>}
                {description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
            </div>
        )}
        {children}
    </section>
);

// Label + description on the left, a switch on the right
export const SwitchRow = ({label, description, checked, onChange, disabled, id}) => (
    <label htmlFor={id} className={`flex items-center justify-between gap-4 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
        <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            {description && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</span>}
        </span>
        <span className="relative inline-flex flex-shrink-0">
            <input id={id} type="checkbox" className="sr-only peer" checked={!!checked} disabled={disabled}
                   onChange={(e) => onChange(e.target.checked)}/>
            <span className="w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-primary transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"/>
            <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"/>
        </span>
    </label>
);

export {Modal, ModalFooter} from '../../stock/StockUI';
export {inputClass} from '../../ClientsTable/clientFormUI';

// "Label :" field of the catalogue forms; error is a plain message
export const FormField = ({id, label, required, error, hint, children}) => (
    <div>
        <label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            {label}{required && <span className="text-red-500"> *</span>} :
        </label>
        {children}
        {error
            ? <p className="text-red-500 text-xs sm:text-sm mt-1">{error}</p>
            : hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
);

// The settings page hands its section title bar to the open section, so a section can
// put its "Enregistrer" there — the catalogue keeps form actions in the header.
export const SectionActionsContext = createContext(null);

export const SectionActions = ({children}) => {
    const target = useContext(SectionActionsContext);
    return target ? createPortal(children, target) : null;
};


// "Enregistrer" in the section title bar; with `form` it submits that form
export const SaveAction = ({onClick, saving, disabled, form, label = 'Enregistrer'}) => (
    <SectionActions>
        <Button variant="plain" size="sm" icon={Save} loading={saving} disabled={disabled}
                type={form ? 'submit' : 'button'} form={form} onClick={form ? undefined : onClick}>
            {label}
        </Button>
    </SectionActions>
);

// Body of a form-like section
export const SettingsForm = ({children, className = ''}) => (
    <div className={`max-w-4xl p-4 sm:p-6 space-y-6 ${className}`}>{children}</div>
);
